import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Form } from 'src/components/common/form/Form';
import { navigateTo } from 'src/lib/navigateTo';
import { BackendRouter } from 'src/lib/routers';
import { events } from 'src/lib/tracking';

interface Props {
  initialState?: Partial<State>;
  returnToUrl: string;
  setLoggingInSSOV2?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ValidationErrors {
  email?: string;
}

interface State {
  email: string;
  errors: ValidationErrors;
}

export const SSOPage = ({
  initialState,
  returnToUrl,
  setLoggingInSSOV2,
}: Props) => {
  const state = {
    email: '',
    errors: {},
    // This is so we can test state in storybook
    ...(initialState || {}),
  };
  const [loggingInSSO, setLoggingInSSO] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(state.email);
  const [errors, setErrors] = useState<ValidationErrors>(state.errors);
  const [idpDestination, setIdpDestination] = useState<URL | undefined>(
    undefined,
  );

  const handleSignInSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    setLoggingInSSO(true);

    try {
      const { destination, errorMessage } = await doLogin(email, returnToUrl);
      if (destination) {
        // Validate the IdP URL before storing: must be a parseable https: URL.
        // An invalid or non-https destination is treated as an error — the
        // modal should never show for a URL we would refuse to navigate to.
        const parsed = parseHttpsUrl(destination);
        if (parsed) {
          setIdpDestination(parsed);
        } else {
          // eslint-disable-next-line no-console
          console.error(
            '[SSO] Blocked redirect to invalid/non-https URL:',
            destination,
          );
          setErrors((oldErrors) => ({
            ...oldErrors,
            email: USER_FACING_ERROR_MESSAGES.UNEXPECTED_ERROR,
          }));
        }
        setLoggingInSSO(false);
        return;
      }
      // Otherwise, show the error message
      setErrors((oldErrors) => ({
        ...oldErrors,
        email: errorMessage || USER_FACING_ERROR_MESSAGES.UNEXPECTED_ERROR,
      }));
    } catch {
      setErrors((oldErrors) => ({
        ...oldErrors,
        email: USER_FACING_ERROR_MESSAGES.UNEXPECTED_ERROR,
      }));
    }

    setLoggingInSSO(false);
  };

  const handleConfirmRedirect = () => {
    if (idpDestination) {
      redirect(idpDestination);
    }
  };

  const handleCancelRedirect = () => {
    setIdpDestination(undefined);
    setLoggingInSSO(false);
  };

  return (
    <>
      <Form
        onSubmit={handleSignInSubmit}
        noValidate
        className="flex flex-col gap-4"
      >
        <FormControl isInvalid={!!errors.email}>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            name="email"
            placeholder="Ex: name@company.com"
            value={email}
            className="w-full"
            onChange={(event) => {
              setEmail(event.target.value);
              setErrors((oldErrors) => ({
                ...oldErrors,
                email: undefined,
              }));
            }}
          />
          <FormErrorMessage>{errors.email}</FormErrorMessage>
        </FormControl>
        <div>
          <Button
            type="submit"
            isDisabled={loggingInSSO}
            isLoading={loggingInSSO}
            size="lg"
            className="w-full"
            variant="primary"
            onClick={() => {
              // calls tracking lib
              /* eslint-disable @typescript-eslint/naming-convention */
              events.login_auth_method_selected.track({
                authorization_method: 'sso',
              });
              /* eslint-enable @typescript-eslint/naming-convention */
            }}
          >
            Continue
          </Button>
        </div>
        <div className="text-center text-sm text-secondary">
          Not using SSO?{' '}
          <ClickableText
            className="text-secondary underline"
            onClick={() => {
              if (setLoggingInSSOV2) setLoggingInSSOV2(false);
            }}
          >
            Click here.
          </ClickableText>
        </div>
      </Form>
      <Modal
        isOpen={!!idpDestination}
        onClose={handleCancelRedirect}
        size="md"
        isCentered={false}
      >
        <ModalOverlay />
        <ModalContent className="mt-[20vh]">
          <ModalHeader>
            <ModalTitle>Redirecting to your identity provider</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-secondary">
              You&apos;re about to leave Apollo Studio to sign in through your
              organization&apos;s identity provider:
            </p>
            {idpDestination && (
              <p className="mt-3 break-all rounded bg-neutral px-3 py-2 font-mono text-xs">
                {idpDestination.hostname}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="secondary" onClick={handleCancelRedirect}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirmRedirect}>
                Continue to sign in
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const USER_FACING_ERROR_MESSAGES = {
  TOO_MANY_CONNECTIONS: 'Please sign in through your identity provider.',
  INVALID_DOMAIN: `SSO is unavailable for this email. Please check the spelling and try again.`,
  UNEXPECTED_ERROR: 'An unexpected error occurred. Please try again.',
};

function toActionableErrorMessage(code: string) {
  const codeToMessage: Record<string, string> = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    too_many_connections: USER_FACING_ERROR_MESSAGES.TOO_MANY_CONNECTIONS,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    invalid_domain: USER_FACING_ERROR_MESSAGES.INVALID_DOMAIN,
    unexpected: USER_FACING_ERROR_MESSAGES.UNEXPECTED_ERROR,
  };

  return codeToMessage[code] || codeToMessage.unexpected;
}

// Returns a parsed URL only if it is a valid absolute https: URL, otherwise
// returns undefined. Used to validate IdP redirect destinations before storing
// or navigating — blocks javascript:, data:, http: and malformed strings.
export function parseHttpsUrl(href: string): URL | undefined {
  try {
    const url = new URL(href);
    return url.protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

type HandleLoginResponseResult = {
  destination?: string;
  errorMessage?: string;
};

export const doLogin = async (
  email: string,
  returnToUrl: string,
): Promise<HandleLoginResponseResult> => {
  const path = BackendRouter.path('SSOLogin', {
    email,
    returnToUrl,
  });

  const response = await fetch(path, {
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'sec-fetch-mode': 'cors',
    },
    method: 'GET',
    mode: 'cors',
  });

  if (response.ok) {
    // If we get back a success, we expect the SSO redirect URL to be returned in the response body
    const responseJson = await response.json();
    const destination = responseJson.redirectUrl;

    if (!destination) {
      return {
        errorMessage: USER_FACING_ERROR_MESSAGES.UNEXPECTED_ERROR,
      };
    }

    return {
      destination,
    };
  } else {
    // If the result is an error, we should show the error message
    // that is returned as json in the response body
    const responseJson = await response.json();
    const message = toActionableErrorMessage(responseJson.code);
    return {
      errorMessage: message,
    };
  }
};

// Defense-in-depth: only navigate to https: URLs. All production IdPs use
// HTTPS, so http: would only occur if the backend returned a malformed or
// compromised value. javascript: and data: are also blocked by this check.
const redirect = (destination: URL) => {
  navigateTo(destination.href);
};
